import React from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FileDown } from "lucide-react";
import { linePrice, lineQty, lineDiscount, lineDiscountType, lineNet, quoteTotals } from "./quote";

/* ---- company info (edit to match your business) ---- */
const COMPANY = {
  name: "Technifro",
  address: "Casablanca, Maroc",
  phone: "+212 661 79 06 08",
  email: "contact@technifro.ma",
  website: "www.technifro.ma",
  logo: "/logotechnifro.png",
};

const ACCENT = [234, 88, 12];

const money = (n) =>
  `${new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0))} MAD`;

const formatDate = (d) =>
  new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const Devis = ({ orderData, label, className, iconSize = 16 }) => {
  const downloadPDF = async () => {
    const order = orderData || {};
    const products = order.products || [];
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210;
    const M = 16;

    // accent top bar
    doc.setFillColor(...ACCENT);
    doc.rect(0, 0, W, 3, "F");

    // logo (optional — same-origin public asset)
    let logo = null;
    try {
      logo = await loadImage(COMPANY.logo);
    } catch {
      logo = null;
    }
    if (logo) {
      const h = 14;
      const w = (logo.width / logo.height) * h || 34;
      doc.addImage(logo, "PNG", M, 12, Math.min(w, 40), h);
    } else {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(20);
      doc.text(COMPANY.name, M, 20);
    }

    // company contact (right)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(20);
    doc.text(COMPANY.name, W - M, 15, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(110);
    doc.text([COMPANY.address, COMPANY.phone, COMPANY.email], W - M, 20, { align: "right" });

    // title
    doc.setTextColor(20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.text("DEVIS", M, 46);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`N° ${order.order_number || "—"}`, M, 53);
    doc.text(`Date : ${order.created_at ? formatDate(order.created_at) : formatDate(new Date())}`, M, 58);

    // client box
    const bx = W - M - 74;
    doc.setDrawColor(228);
    doc.setFillColor(249, 249, 250);
    doc.roundedRect(bx, 40, 74, 30, 2, 2, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(130);
    doc.text("CLIENT", bx + 5, 46);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(35);
    const clientLines = [
      `${order.first_name || ""} ${order.last_name || ""}`.trim(),
      order.company_name,
      [order.city, order.phone].filter(Boolean).join(" · "),
      order.email,
    ].filter(Boolean);
    doc.text(clientLines.length ? clientLines : ["—"], bx + 5, 51);

    // line items
    const rows = products.map((p) => {
      const variant = (p.optionValues || p.option_values || [])
        .map((ov) => ov.value)
        .join(" / ");
      const name = variant ? `${p.name} (${variant})` : p.name;
      const disc = lineDiscount(p);
      const discLabel = disc > 0 ? (lineDiscountType(p) === "percent" ? `${disc}%` : money(disc)) : "—";
      return [
        name,
        String(lineQty(p)),
        money(linePrice(p)),
        discLabel,
        money(lineNet(p)),
      ];
    });

    autoTable(doc, {
      startY: 76,
      head: [["Désignation", "Qté", "P.U. HT", "Remise", "Total HT"]],
      body: rows.length ? rows : [["Aucun article", "", "", "", ""]],
      theme: "grid",
      styles: { lineColor: [232, 232, 234], lineWidth: 0.1, cellPadding: 2.5 },
      headStyles: { fillColor: [28, 28, 31], textColor: 255, fontSize: 9, fontStyle: "bold", halign: "left" },
      bodyStyles: { fontSize: 9, textColor: 45 },
      alternateRowStyles: { fillColor: [250, 250, 251] },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { halign: "center", cellWidth: 16 },
        2: { halign: "right", cellWidth: 28 },
        3: { halign: "center", cellWidth: 20 },
        4: { halign: "right", cellWidth: 30 },
      },
      margin: { left: M, right: M },
    });

    const { subtotal, globalDiscount, total, vatRate, vatAmount, totalTTC } = quoteTotals(order);

    let ty = (doc.lastAutoTable?.finalY || 120) + 10;
    const tx = W - M - 74;

    doc.setFontSize(9.5);
    const totRow = (k, v, strong) => {
      doc.setFont("helvetica", strong ? "bold" : "normal");
      doc.setTextColor(strong ? 30 : 110);
      doc.text(k, tx, ty);
      doc.setTextColor(strong ? 30 : 45);
      doc.text(v, W - M, ty, { align: "right" });
      ty += 6.5;
    };
    totRow("Sous-total HT", money(subtotal));
    if (globalDiscount > 0) {
      const lbl = order.discount_type === "percent" ? `Remise (${Number(order.discount)}%)` : "Remise";
      totRow(lbl, `- ${money(globalDiscount)}`);
    }
    if (vatRate > 0) {
      totRow("Total HT", money(total));
      totRow(`TVA (${vatRate}%)`, money(vatAmount));
    }

    // total line
    const grand = vatRate > 0 ? totalTTC : total;
    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(0.5);
    doc.line(tx, ty - 2, W - M, ty - 2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12.5);
    doc.setTextColor(...ACCENT);
    doc.text(vatRate > 0 ? "TOTAL TTC" : "TOTAL", tx, ty + 5);
    doc.text(money(grand), W - M, ty + 5, { align: "right" });

    // notes
    if (order.comments) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(120);
      doc.text("Note :", M, ty + 2);
      doc.text(doc.splitTextToSize(order.comments, 90), M, ty + 7);
    }

    // footer
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`${COMPANY.name} · ${COMPANY.website} · ${COMPANY.phone}`, W / 2, 285, { align: "center" });
    doc.text("Devis valable 30 jours — Merci de votre confiance.", W / 2, 290, { align: "center" });

    doc.save(`devis-${order.order_number || "draft"}.pdf`);
  };

  return (
    <button
      type="button"
      onClick={downloadPDF}
      className={className || "flex items-center justify-center rounded-md p-1 text-muted transition hover:bg-surface-2 hover:text-ink"}
    >
      <FileDown size={iconSize} />
      {label ? <span>{label}</span> : null}
    </button>
  );
};

export default Devis;
